import React from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MapViewProvider } from './context/MapViewContext';
import { PourNavProvider } from './context/PourNavContext';
import { MobileHeaderProvider } from './context/MobileHeaderContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { aboveBottomBar } from './components/mobile/mobileChrome';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import { isSuperAdmin, hasPermission } from './utils/userPermissions';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import NewMember from './pages/NewMember';
import DashboardLayout from './components/DashboardLayout';
import ErrorBoundary from './components/ErrorBoundary';
import Pours from './pages/Pours';
import Hardware from './pages/Hardware';
import HardwareForm from './pages/HardwareForm';
import Companies from './pages/Companies';
import CompanyView from './pages/CompanyView';
import CompanyEdit from './pages/CompanyEdit';
import CompanyUsers from './pages/CompanyUsers';
import Users from './pages/Users';
import UserEdit from './pages/UserEdit';
import ChangePassword from './pages/ChangePassword';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProjectEdit from './pages/ProjectEdit';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import ProductEdit from './pages/ProductEdit';
import CalibrationPage from './pages/CalibrationPage';
import MassConcreteLimits from './pages/MassConcreteLimits';
import MassConcreteLimitForm from './pages/MassConcreteLimitForm';
import EvaporationLimits from './pages/EvaporationLimits';
import EvaporationLimitForm from './pages/EvaporationLimitForm';
import MaturityInputs from './pages/MaturityInputs';
import BatchingPlants from './pages/BatchingPlants';
import BatchingPlantForm from './pages/BatchingPlantForm';
import BatchingPlantDetail from './pages/BatchingPlantDetail';
import { PourDashboardPage } from './pages/dashboard/PourDashboardPage';
import PourSetup from './pages/PourSetup';
import PourSetupLegacy from './pages/PourSetupLegacy';
import PourCreate from './pages/PourCreate';
import SimulatorPage from './pages/admin/SimulatorPage';
import Settings from './pages/Settings';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';

// The phone and tablet layout's touch sizes apply below md (900px), where
// the app shows its mobile shell; the larger html font stays phone only.
const BELOW_MD = '@media (max-width: 899.95px)';

// Bottom-anchored snackbars sit 8px above the mobile bottom bar and the iOS
// home bar (top-anchored ones keep MUI's place).
const snackbarAboveBottomBar = {
  [BELOW_MD]: {
    bottom: aboveBottomBar(8),
  },
};

// Create theme — green/olive accent, dark sidebar, white header
const theme = createTheme({
  palette: {
    primary: {
      main: '#79A82A',
      light: '#9AD02E',
      dark: '#5E8A1E',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#1a1a2e',
    },
    // "Active"/positive-status colour used throughout the app (status chips,
    // row indicators). Reuses the brand green family instead of MUI's stock
    // institutional dark green (#2e7d32) — main is the primary "dark" shade
    // rather than primary.main itself so filled chips keep legible white text.
    success: {
      light: '#79A82A',
      main: '#5E8A1E',
      dark: '#496B17',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    body2: { fontSize: '0.875rem' },
    caption: { fontSize: '0.8rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@media (max-width: 600px)': {
          html: {
            fontSize: '17px',
          },
        },
        // Date/time picker popups otherwise blend into the page background
        '.MuiPickersPopper-paper': {
          border: '1px solid #d0d0d0',
          boxShadow: '0 8px 28px rgba(0,0,0,0.22)',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#333333',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          textTransform: 'none',
          fontSize: '0.875rem',
          minHeight: 40,
        },
        sizeSmall: {
          minHeight: 34,
          fontSize: '0.8rem',
        },
        // Primary call-to-action buttons (New Pour, Add Hardware, Save, dialog
        // confirms) are black to match the Sign In button, with a soft drop
        // shadow so they read as floating above the page. Hover lifts them a
        // touch further; pressing settles them back down.
        containedPrimary: {
          backgroundColor: '#000000',
          color: '#ffffff',
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.28), 0 1px 3px rgba(0,0,0,0.18)',
          transition: 'background-color 150ms ease, box-shadow 150ms ease, transform 150ms ease',
          '&:hover': {
            backgroundColor: '#222222',
            boxShadow: '0 8px 20px rgba(0,0,0,0.32), 0 2px 6px rgba(0,0,0,0.2)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            backgroundColor: '#000000',
            color: '#ffffff',
            opacity: 0.45,
            boxShadow: 'none',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          [BELOW_MD]: {
            padding: 10,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '@media (max-width: 600px)': {
            fontSize: '0.85rem',
            padding: '8px 12px',
          },
        },
        head: {
          backgroundColor: '#fafafa',
          fontWeight: 600,
          color: '#666',
          borderBottom: '2px solid #e0e0e0',
          fontSize: '0.8rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.8rem',
        },
        sizeSmall: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          [BELOW_MD]: {
            fontSize: '1rem',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          fontSize: '0.85rem',
          minHeight: 32,
          paddingTop: 4,
          paddingBottom: 4,
        },
        listbox: {
          padding: 4,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
          minHeight: 32,
          paddingTop: 4,
          paddingBottom: 4,
          [BELOW_MD]: {
            fontSize: '1rem',
            minHeight: 44,
          },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        anchorOriginBottomCenter: snackbarAboveBottomBar,
        anchorOriginBottomLeft: snackbarAboveBottomBar,
        anchorOriginBottomRight: snackbarAboveBottomBar,
      },
    },
  },
});

/** A render error anywhere below shows a message with a reload button
 *  instead of a blank page; moving to another page clears it. */
const RoutedErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
};

// App Routes Component
const UserViewRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/users/${id}/edit`} replace />;
};

const HardwareEditRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/hardware/${id}`} replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <MapViewProvider>
    <PourNavProvider>
    <MobileHeaderProvider>
    <NotificationsProvider>
    <Routes>
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } 
      />
      <Route
        path="/forgot-password"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />
        }
      />
      <Route
        path="/new-member"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <NewMember />
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Pours />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/pours" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Pours />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/pours/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PourCreate />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pours/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PourSetup />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pours/:id/edit-legacy"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PourSetupLegacy />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pours/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PourSetup />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pours/:pourId/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PourDashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hardware"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Hardware />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hardware/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <HardwareForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hardware/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <HardwareForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Viewing and editing a device share one page */}
      <Route path="/hardware/:id/edit" element={<HardwareEditRedirect />} />
      <Route 
        path="/contacts" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <div>{t('nav.contacts')} ({t('common.comingSoon')})</div>
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Projects />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects/new" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProjectEdit />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects/:projectId" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProjectDetail />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/projects/:id/edit" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProjectEdit />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/suppliers"
        element={<Navigate to="/batching-plants" replace />}
      />
      <Route
        path="/batching-plants"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <BatchingPlants />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batching-plants/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <BatchingPlantForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batching-plants/:plantId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <BatchingPlantDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/batching-plants/:plantId/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <BatchingPlantForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Products />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProductEdit />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:productId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProductDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProductEdit />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calibrations/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CalibrationPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calibrations/:calId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CalibrationPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute check={isSuperAdmin}>
              <DashboardLayout>
                <div>{t('nav.reports')} ({t('common.comingSoon')})</div>
              </DashboardLayout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/simulator"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute check={isSuperAdmin}>
              <DashboardLayout>
                <SimulatorPage />
              </DashboardLayout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      {/* Standalone Users page is super-admin only; clients manage users
          via the Users tab inside Settings (which embeds the same page).
          The /users/:id sub-routes stay open so that tab's row actions work. */}
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute check={isSuperAdmin}>
              <DashboardLayout>
                <Users />
              </DashboardLayout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/new"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute check={(u) => hasPermission(u, 'users.create')}>
              <DashboardLayout>
                <UserEdit />
              </DashboardLayout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      {/* The read-only user page is gone: viewing and editing share one form */}
      <Route path="/users/:id" element={<UserViewRedirect />} />
      <Route
        path="/users/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <UserEdit />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/users/:id/change-password" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ChangePassword />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute check={isSuperAdmin}>
              <DashboardLayout>
                <Companies />
              </DashboardLayout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/new"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute check={isSuperAdmin}>
              <DashboardLayout>
                <CompanyEdit />
              </DashboardLayout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CompanyView />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/:companyId/edit"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute check={isSuperAdmin}>
              <DashboardLayout>
                <CompanyEdit />
              </DashboardLayout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/:companyId/users"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute check={isSuperAdmin}>
              <DashboardLayout>
                <CompanyUsers />
              </DashboardLayout>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      {/* Units of Measure now lives in Settings; keep the old URL working */}
      <Route path="/data-maintenance" element={<Navigate to="/settings/units" replace />} />
      <Route
        path="/mass-concrete-limits"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MassConcreteLimits />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mass-concrete-limits/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MassConcreteLimitForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mass-concrete-limits/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MassConcreteLimitForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mass-concrete-limits/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MassConcreteLimitForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Environmental Limits (temperature & humidity sensors): page not built yet */}
      <Route
        path="/environmental-limits"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <div>{t('nav.environmentalLimits')} ({t('common.comingSoon')})</div>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Concrete Strength Targets live on each mix design (its target
          groups), so the old placeholder page is off the menu and an old
          link or bookmark goes to the mix designs list. */}
      <Route path="/maturity-targets" element={<Navigate to="/products" replace />} />
      <Route
        path="/evaporation-limits"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EvaporationLimits />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evaporation-limits/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EvaporationLimitForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evaporation-limits/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EvaporationLimitForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evaporation-limits/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EvaporationLimitForm />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <div>{t('nav.help')} ({t('common.comingSoon')})</div>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={<Navigate to="/settings/client-details" replace />}
      />
      <Route
        path="/settings/:section"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Concrete Maturity Inputs: Nurse-Saul and Arrhenius groups */}
      <Route
        path="/maturity-inputs"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MaturityInputs />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </NotificationsProvider>
    </MobileHeaderProvider>
    </PourNavProvider>
    </MapViewProvider>
  );
};

// A data router (rather than BrowserRouter) so a page can ask before it is
// left: useBlocker works only in one. The route table stays in AppRoutes,
// under a single catch-all route, so every path resolves as before.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <RoutedErrorBoundary>
        <AppRoutes />
      </RoutedErrorBoundary>
    ),
  },
]);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
